import { Publication } from './types';

/**
 * Add publications here — the page groups them by year automatically,
 * newest first, and renders a link button for each URL that is present.
 *
 * Example:
 * {
 *   title: 'A conversational interface for Earth observation',
 *   authors: ['Ashraf, S.', 'Coauthor, A.'],
 *   venue: 'Remote Sensing of Environment',
 *   year: 2026,
 *   abstract: 'One paragraph, plain language.',
 *   tags: ['LLM', 'Sentinel-2', 'NDVI'],
 *   doi: 'https://doi.org/10.0000/example',
 *   pdfUrl: 'https://example.org/paper.pdf',
 *   codeUrl: 'https://github.com/saeedashraf/example',
 * },
 */
export const PUBLICATIONS: Publication[] = [];

/**
 * Open-source release of the LLEO agent (github.com/saeedashraf/lleo-agent).
 * Rendered as a card on the Publications page.
 */
export const OPEN_SOURCE = {
  name: 'lleo-agent',
  repoUrl: 'https://github.com/saeedashraf/lleo-agent',
  issuesUrl: 'https://github.com/saeedashraf/lleo-agent/issues',
  paperReadmeUrl: 'https://github.com/saeedashraf/lleo-agent/blob/main/README_paper.md',
  summary:
    'LLEO Agent is the open-source core of LLEO: an agentic Earth-observation toolkit in which a ' +
    'large language model acts as the coordinator and six satellite and geospatial capabilities ' +
    '(Sentinel-2 NDVI land-use change, Sentinel-5P air quality, Sentinel-2 water quality, monthly ' +
    'timelapse animation, weather and nearby-business context) are exposed as tools behind a single ' +
    'local Model Context Protocol (MCP) server. The agent resolves locations, dates and indicators ' +
    'from a plain-language question, dispatches to specialist sub-agents and reusable skills, checks ' +
    'the result and iterates before answering, and every tool writes reproducible artifacts ' +
    '(interactive maps with legends, multi-panel charts, GIFs) to disk. The repository also ships the ' +
    'research pipeline behind our claim-verification paper — a batch runner that routes thousands of ' +
    'corporate environmental claims to the tools and derives rule-based TRUE / FALSE / UNCERTAIN ' +
    'verdicts from Sentinel-2 NDVI change, plus figure, statistics, cross-year and LLM-as-judge ' +
    'scripts — so the published results can be reproduced and extended to new claim vintages.',
  tools: [
    { name: 'lleo_ndvi', source: 'Sentinel-2', what: 'vegetation / land-use change' },
    { name: 'lleo_air_quality', source: 'Sentinel-5P', what: 'NO₂ · SO₂ · CO · O₃ · CH₄' },
    { name: 'lleo_water_quality', source: 'Sentinel-2', what: 'NDWI, turbidity, chlorophyll, TSM, CDOM' },
    { name: 'lleo_animation', source: 'Sentinel-2', what: 'monthly-median timelapse GIF' },
    { name: 'lleo_weather', source: 'OpenWeather', what: 'current + 5-day forecast' },
    { name: 'lleo_business', source: 'Google Places', what: 'nearby businesses / POIs' }
  ],
  contribute: [
    {
      title: 'Add a new tool',
      text: 'Drop a module in tools/ that returns the standard result dict (status, summary, files, data), register it with one @mcp.tool() in mcp_server/server.py, and add a case to test_endpoints.py.'
    },
    {
      title: 'Share a skill',
      text: 'Turn a repeatable analysis (dNBR burn severity, NDBI built-up index, DEM slope …) into a playbook under .claude/skills/<name>/SKILL.md with its script, so the agent can run it in one call.'
    },
    {
      title: 'Extend the paper pipeline',
      text: 'Run claims_runner.py on a new claim vintage (e.g. 2026), add a verdict rule or indicator, or contribute a human / LLM second-rater study — see README_paper.md for the data schema.'
    },
    {
      title: 'Report & review',
      text: 'Open an issue for bugs, data gaps or feature ideas, and send pull requests against main. Run python test_endpoints.py before submitting; never commit .env or credential JSON files.'
    }
  ]
};
